import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";


type Tournament = {
  id: string;
  name: string;
  year: number;
  slug: string;
  is_active: boolean;
};

type TournamentWithStats = Tournament & {
  matches_count: number;
  next_kickoff: string | null;
};

export function AvailableTournaments() {
  const { t, i18n } = useTranslation();
  const [tournaments, setTournaments] = useState<TournamentWithStats[] | null>(null);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: ts } = await supabase
        .from("tournaments")
        .select("id, name, year, slug, is_active")
        .eq("is_active", true)
        .order("year", { ascending: false });

      if (!ts || cancelled) {
        if (!cancelled) setTournaments([]);
        return;
      }

      const withStats = await Promise.all(
        ts.map(async (t) => {
          const { count } = await supabase
            .from("matches")
            .select("id", { count: "exact", head: true })
            .eq("tournament_id", t.id);

          const { data: next } = await supabase
            .from("matches")
            .select("kickoff_at")
            .eq("tournament_id", t.id)
            .gte("kickoff_at", new Date().toISOString())
            .order("kickoff_at", { ascending: true })
            .limit(1)
            .maybeSingle();

          return {
            ...t,
            matches_count: count ?? 0,
            next_kickoff: next?.kickoff_at ?? null,
          };
        })
      );

      if (!cancelled) setTournaments(withStats);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (tournaments && tournaments.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-20">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-3">
          {t("landing.tournaments.title")} <span className="text-primary">{t("landing.tournaments.title2")}</span>
        </h2>
        <p className="text-center text-pitch-cream/65 mb-12">
          {t("landing.tournaments.subtitle")}
        </p>

        {!tournaments ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="border border-pitch rounded-2xl p-6 bg-white/[0.02] h-40 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {tournaments.map((tour) => (
              <div
                key={tour.id}
                className="border border-pitch rounded-2xl p-6 bg-white/[0.02] backdrop-blur flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                      <Trophy className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg leading-tight">{tour.name}</h3>
                      <div className="text-xs text-pitch-cream/55 mt-0.5">{t("landing.tournaments.year", { year: tour.year })}</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-primary border border-primary/40 rounded-full px-2 py-0.5">
                    {t("landing.tournaments.open")}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm text-pitch-cream/70 mb-5">
                  <span>
                    <strong className="text-pitch-cream">{tour.matches_count}</strong> {t("landing.tournaments.matches")}
                  </span>
                  {tour.next_kickoff && (
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 text-primary" />
                      {new Date(tour.next_kickoff).toLocaleDateString(i18n.language === "en" ? "en-GB" : "cs-CZ", {
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                  )}
                </div>

                <Button
                  asChild
                  className="mt-auto font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Link to="/auth">{t("landing.tournaments.play", { name: tour.name })}</Link>
                </Button>
              </div>
            ))}
          </div>

        )}
      </div>
    </section>
  );
}
